import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions, useUser } from '@paintscout/ui-business'
import AddBoxIcon from '@material-ui/icons/AddBox'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import ConvertIcon from '@material-ui/icons/SwapHoriz'
import ListIcon from '@material-ui/icons/List'
import get from 'lodash/get'
import React, { useMemo } from 'react'
import type { RenderableItem } from '@paintscout/util/builder'
import { getFeatures, getItems, getObjectLabels } from '@paintscout/util/builder'
import { hasCustomTotal } from '@paintscout/util/'
import { useQuote } from '../context'
import ItemRowContent from './ItemRowContent'
import OutlinedItemTable from './OutlinedItemTable'
import type { OutlinedItemTableProps } from './OutlinedItemTable'
import OutlinedTableCard from './OutlinedTableCard'

export interface ArchivedTableNextProps {
  /** Tier-1 section strip title (the big "Archived" heading + subtitle block). */
  sectionTitle?: React.ReactNode
  /** Right-aligned content in the section strip (the Area/Surface view toggle). */
  sectionAction?: React.ReactNode
  selectedItems?: OutlinedItemTableProps['selectedItems']
  onItemSelection?: OutlinedItemTableProps['onItemSelection']
  onItemAction?: (action: string, section: 'archived', item: RenderableItem) => void
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {},
    // Extra breathing room around the "Archived" section strip, matching the prototype. `!important`
    // because the base padding comes from a sibling `useStyles` call in `OutlinedTableCard` — JSS gives
    // no ordering guarantee between separate sheets, so specificity is the only reliable override.
    headerStrip: {
      padding: `${theme.spacing(2)}px ${theme.spacing(1.5)}px ${theme.spacing(2)}px ${theme.spacing(2)}px !important`,
      backgroundColor: theme.palette.background.paper
    },
    // Centered empty state inside the card body, matching the legacy ArchivedTable copy.
    noResults: {
      textAlign: 'center',
      padding: theme.spacing(5, 2)
    },
    noResultsSubtitle: {
      color: theme.paintscout.palette.brand.Neutral.P60,
      marginTop: theme.spacing(0.5)
    }
  }),
  { name: 'ArchivedTableNext' }
)

/**
 * The Archived section for the new (estimateNext) pipeline, rendered in the outlined-card style.
 * Ported from the legacy `ArchivedTable` (same item building, action arrays, and empty state) but
 * rendered through `OutlinedItemTable` / `OutlinedTableCard`, reusing `ItemRowContent` for the row
 * content. The big "Archived" heading + view toggle live in the card's section strip and are
 * supplied by the view wrapper via `sectionTitle` / `sectionAction`.
 */
export default function ArchivedTableNext({
  sectionTitle,
  sectionAction,
  selectedItems,
  onItemSelection,
  onItemAction
}: ArchivedTableNextProps) {
  const classes = useStyles()
  const { quote, tableView, isEditable, onItemAction: contextItemAction } = useQuote()
  const { options } = useClientOptions()
  const { isOfficeUser, isCrew } = useUser()

  const features = getFeatures({ options })
  const canConvertToLineItem = get(features, 'quotes.convertToLineItem')
  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })

  const disableSubstrateView = hasCustomTotal(quote.areas, true)
  const view = disableSubstrateView ? 'area' : tableView

  const items: RenderableItem[] = useMemo(
    () =>
      getItems({
        quote,
        section: 'archived',
        view,
        consumer: 'customer',
        options,
        overrideOptions: { showPrices: true }
      }).map((item) => {
        if (!item.description && !item.value && item.subItems && item.subItems.length > 0) {
          const firstSubItem = item.subItems.shift()
          return {
            ...item,
            description: { useCustom: false, default: firstSubItem.description, custom: '' },
            value: firstSubItem.value
          }
        }
        return item
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quote, view]
  )

  const showActions = isEditable || isOfficeUser || isCrew
  const handleItemAction: (action: string, section: 'archived', item: RenderableItem) => void =
    onItemAction ?? ((action, _section, item) => contextItemAction?.(action, 'archived', [item.key]))

  if (items.length === 0) {
    return (
      <OutlinedTableCard
        className={classes.root}
        sectionTitle={sectionTitle}
        sectionAction={sectionAction}
        sectionStripClassName={classes.headerStrip}
        hideHeader
        singleBorder
      >
        <div className={classes.noResults}>
          <Typography variant="h4">
            There are no archived items on this {objectLabels.quote.value.toLowerCase()}.
          </Typography>
          <Typography variant="subtitle1" className={classes.noResultsSubtitle}>
            Items can be archived by selecting &quot;Archive&quot; from the Item Action menu.
          </Typography>
        </div>
      </OutlinedTableCard>
    )
  }

  return (
    <OutlinedItemTable
      className={classes.root}
      sectionTitle={sectionTitle}
      sectionAction={sectionAction}
      sectionStripClassName={classes.headerStrip}
      headerVariant="light"
      singleBorder
      title={'Item'}
      headerRight={objectLabels.currency.symbol}
      draggable={false}
      checkboxes={isEditable}
      showAddButton={false}
      items={items}
      selectedItems={selectedItems}
      section={'archived'}
      onItemSelection={onItemSelection}
      onItemClick={isEditable ? (_ev, item) => handleItemAction('edit', 'archived', item as RenderableItem) : undefined}
      renderRowColumns={(item: RenderableItem) => {
        const actions = []

        actions.push({ key: 'edit', label: 'Edit', icon: EditIcon })

        if (!isOfficeUser && !isCrew) {
          actions.push({ key: 'move-to-bid', label: `Move to ${objectLabels.quote.value}`, icon: AddBoxIcon })
        }

        actions.push({ key: 'make-option', label: `Make ${objectLabels.option.value}`, icon: AddBoxIcon })

        if (item.type === 'group') {
          actions.push({ key: 'ungroup', label: 'Ungroup', icon: ListIcon })
        }

        if (canConvertToLineItem && item.type !== 'line_item') {
          actions.push({ key: 'convert', label: 'Convert to Line Item', icon: ConvertIcon })
        }

        // Last, as the one destructive action — the prototype puts Delete at the foot of every item
        // menu (ENG-1922). Only its position moved; the area-row gate it already carried is kept.
        if (item.type !== 'group' && view === 'area') {
          actions.push({ key: 'delete', label: 'Delete', icon: DeleteIcon })
        }

        return (
          <ItemRowContent
            item={item}
            section={'archived'}
            isEditable={showActions}
            showAmount
            actions={showActions ? actions : undefined}
            onAction={(action, it) => handleItemAction(action, 'archived', it)}
          />
        )
      }}
    />
  )
}
